import { z } from "zod";
import { MAX_CARGO_UNITS } from "./cargo-stacks.js";
import { hullModelIdSchema } from "./hull-models.js";

export const hullRevisionSchema = z.number().int().min(0).max(2_147_483_647);
export const supplyHullContextSchema = z.strictObject({ hullId: hullModelIdSchema, hullRevision: hullRevisionSchema });

export const SUPPLY_PRICE_VERSION = "supplies-20261027-v1";
export const SUPPLY_IDS = ["battery", "battery-II", "battery-III", "rocket", "rocket-II", "rocket-III", "drone", "drone-II", "drone-III"] as const;
export const SUPPLY_KINDS = ["battery", "rocket", "drone"] as const;
export type SupplyKind = (typeof SUPPLY_KINDS)[number];
export type SupplyId = (typeof SUPPLY_IDS)[number];
const quantitySchema = z.number().int().min(1).max(MAX_CARGO_UNITS);
export const supplyPurchaseInputSchema = z.strictObject({
  requestId: z.string().min(8).max(100),
  supplyId: z.enum(SUPPLY_IDS),
  quantity: quantitySchema,
  priceVersion: z.string().min(1).max(100),
});
export const supplyTransferInputSchema = z.strictObject({
  ...supplyHullContextSchema.shape,
  requestId: z.string().min(8).max(100),
  runId: z.uuid(),
  supplyId: z.enum(SUPPLY_IDS),
  direction: z.enum(["load", "unload"]),
  quantity: quantitySchema,
});
export type SupplyPurchaseInput = z.infer<typeof supplyPurchaseInputSchema>;
export type SupplyTransferInput = z.infer<typeof supplyTransferInputSchema>;
export const supplyUseInputSchema = z.strictObject({
  ...supplyHullContextSchema.shape,
  requestId: z.string().min(8).max(100),
  runId: z.uuid(),
  lifeSeq: z.number().int().nonnegative(),
  itemId: z.string().min(1).max(200),
  targetId: z.string().min(1).max(200).optional(),
});
export type SupplyUseInput = z.infer<typeof supplyUseInputSchema>;

export const batteryTermsSchema = z.strictObject({
  format: z.literal(1),
  algorithm: z.literal("battery-v1"),
  supplyId: z.enum(["battery", "battery-II", "battery-III"]).optional(),
  energy: z.number().finite().positive().max(100_000),
  cooldownSeconds: z.number().finite().positive().max(300),
  priceVersion: z.string().min(1).max(100),
  unitCost: z.strictObject({
    credits: z.string().regex(/^(0|[1-9]\d*)$/),
    components: z.number().int().nonnegative(),
  }).readonly(),
}).readonly();
export type BatteryTerms = z.infer<typeof batteryTermsSchema>;
const supplyCost = z.strictObject({ credits: z.string().regex(/^(0|[1-9]\d*)$/), components: z.number().int().nonnegative() }).readonly();
const supplyBase = { format: z.literal(1), cooldownSeconds: z.number().finite().positive().max(300), priceVersion: z.string().min(1).max(100), unitCost: supplyCost };
export const rocketTermsSchema = z.strictObject({ ...supplyBase, algorithm: z.literal("rocket-v1"),
  supplyId: z.enum(["rocket", "rocket-II", "rocket-III"]), damage: z.number().finite().positive().max(10000), range: z.number().finite().positive().max(2000) }).readonly();
export const droneTermsSchema = z.strictObject({ ...supplyBase, algorithm: z.literal("drone-v1"),
  supplyId: z.enum(["drone", "drone-II", "drone-III"]), healingPerSecond: z.number().finite().positive().max(100), duration: z.number().finite().positive().max(120) }).readonly();
export const supplyTermsSchema = z.union([batteryTermsSchema, rocketTermsSchema, droneTermsSchema]);
export type SupplyTerms = z.infer<typeof supplyTermsSchema>;
export type RocketTerms = z.infer<typeof rocketTermsSchema>;
export type DroneTerms = z.infer<typeof droneTermsSchema>;
export function supplyKind(terms: SupplyTerms): SupplyKind { return terms.algorithm === "battery-v1" ? "battery" : terms.algorithm === "rocket-v1" ? "rocket" : "drone"; }
export function supplySku(terms: SupplyTerms): SupplyId { return terms.supplyId ?? "battery"; }
export const supplyUseReceiptSchema = z.strictObject({
  hullId: hullModelIdSchema.nullable(),
  hullRevision: hullRevisionSchema.nullable(),
  requestId: z.string().min(8).max(100),
  runId: z.uuid(),
  lifeSeq: z.number().int().nonnegative(),
  itemId: z.string().min(1).max(200),
  instanceId: z.string().min(1).max(100),
  usedAt: z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER),
  cooldownUntil: z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER),
  targetId: z.string().min(1).max(200).optional(),
  terms: supplyTermsSchema,
}).refine(receipt => (receipt.hullId === null) === (receipt.hullRevision === null), "Incomplete supply hull context");
export type SupplyUseReceipt = z.infer<typeof supplyUseReceiptSchema>;

export const BATTERY_TERMS: BatteryTerms = batteryTermsSchema.parse({
  format: 1,
  algorithm: "battery-v1",
  energy: 60,
  cooldownSeconds: 15,
  priceVersion: SUPPLY_PRICE_VERSION,
  unitCost: { credits: "200", components: 1 },
});

export function captureBatteryTerms(): BatteryTerms {
  return batteryTermsSchema.parse(BATTERY_TERMS);
}

export const SUPPLY_CATALOG = [
  { id: "battery", kind: "battery", name: "Батарея I", terms: BATTERY_TERMS },
  { id: "battery-II", kind: "battery", name: "Батарея II", terms: batteryTermsSchema.parse({ ...BATTERY_TERMS, supplyId: "battery-II", energy: 90, unitCost: { credits: "600", components: 3 } }) },
  { id: "battery-III", kind: "battery", name: "Батарея III", terms: batteryTermsSchema.parse({ ...BATTERY_TERMS, supplyId: "battery-III", energy: 140, unitCost: { credits: "1600", components: 8 } }) },
  ...([0, 1, 2] as const).map(tier => ({ id: (["rocket", "rocket-II", "rocket-III"] as const)[tier], kind: "rocket" as const, name: `Ракета ${"I".repeat(tier + 1)}`,
    terms: rocketTermsSchema.parse({ format: 1, algorithm: "rocket-v1", supplyId: ["rocket", "rocket-II", "rocket-III"][tier],
      damage: [80, 140, 220][tier], range: 1000, cooldownSeconds: 3, priceVersion: SUPPLY_PRICE_VERSION,
      unitCost: { credits: ["150", "450", "1200"][tier], components: [1, 3, 8][tier] } }) })),
  ...([0, 1, 2] as const).map(tier => ({ id: (["drone", "drone-II", "drone-III"] as const)[tier], kind: "drone" as const, name: `Ремонтный дрон ${"I".repeat(tier + 1)}`,
    terms: droneTermsSchema.parse({ format: 1, algorithm: "drone-v1", supplyId: ["drone", "drone-II", "drone-III"][tier],
      healingPerSecond: [2, 4, 7][tier], duration: 25, cooldownSeconds: 45, priceVersion: SUPPLY_PRICE_VERSION,
      unitCost: { credits: ["300", "900", "2400"][tier], components: [2, 6, 16][tier] } }) })),
] satisfies { id: SupplyId; kind: SupplyKind; name: string; terms: SupplyTerms }[];
export function supplyDefinition(id: SupplyId) {
  const definition = SUPPLY_CATALOG.find(item => item.id === id);
  if (!definition) throw new Error(`Missing supply ${id}`);
  return definition;
}
export function captureSupplyTerms(id: SupplyId): SupplyTerms { return supplyTermsSchema.parse(supplyDefinition(id).terms); }
